/**
 * Renders whatever `confirmDialogStore` is currently asking. Mounted once at
 * the root; renders nothing until there is a question. Only the web build ever
 * populates the store (native keeps `Alert.alert`), so on native this is inert.
 */
import React from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { useConfirmDialogStore } from '../stores/confirmDialogStore';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

export default function ConfirmDialogHost() {
  const current = useConfirmDialogStore((s) => s.current);
  const settle = useConfirmDialogStore((s) => s.settle);
  const styles = useThemedStyles(createStyles);

  if (!current) return null;

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={() => settle(false)}
      accessibilityViewIsModal={true}
    >
      <View style={styles.overlay}>
        <View style={styles.card} testID="confirm-dialog">
          <Text style={styles.title} accessibilityRole="header">
            {current.title}
          </Text>
          <Text style={styles.message}>{current.message}</Text>

          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => settle(false)}
              activeOpacity={0.7}
              accessibilityLabel={current.cancelText}
              accessibilityRole="button"
              testID="confirm-dialog.cancel-button"
            >
              <Text style={styles.cancelText} accessible={false}>
                {current.cancelText}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmButton, current.destructive && styles.confirmButtonDestructive]}
              onPress={() => settle(true)}
              activeOpacity={0.7}
              accessibilityLabel={current.confirmText}
              accessibilityRole="button"
              testID="confirm-dialog.confirm-button"
            >
              <Text style={styles.confirmText} accessible={false}>
                {current.confirmText}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  overlay: { flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'center' as const, alignItems: 'center' as const, padding: 24 },
  card: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, width: '100%' as const, maxWidth: 420 },
  title: { fontSize: 18, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 8, textAlign: 'center' as const },
  message: { fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' as const, lineHeight: 20, marginBottom: 20 },
  actions: { flexDirection: 'row' as const, justifyContent: 'flex-end' as const, gap: 10 },
  cancelButton: { borderWidth: 1, borderColor: theme.colors.borderMedium, borderRadius: 10, paddingVertical: 12, paddingHorizontal: 18, alignItems: 'center' as const },
  cancelText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.textSecondary },
  confirmButton: { backgroundColor: theme.colors.primary, borderRadius: 10, paddingVertical: 12, paddingHorizontal: 18, alignItems: 'center' as const },
  confirmButtonDestructive: { backgroundColor: theme.colors.danger },
  confirmText: { fontSize: 15, fontWeight: '700' as const, color: theme.colors.textOnPrimary },
});
